import {
  Link,
  Typography,
  Grid2,
  Divider,
  useMediaQuery,
  Theme,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import React from "react";
import { useTheme } from "@mui/material/styles";
import styled from "@emotion/styled";
import { useMandatorContext } from "@rentablo/theming";
import { Logo } from "@rentablo/components";

const Wrapper = styled("div")(({ theme }) => ({
  background: theme.palette.background.footer,
  padding: theme.spacing(14, 30, 11, 30),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(14, 3, 11, 3),
  },
}));

const StyledLogo = styled(Logo)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const Copyright = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.tertiary,
}));

const Grid2Container = styled(Grid2)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    order: 1,
  },
  [theme.breakpoints.only("xs")]: {
    gap: theme.spacing(4),
  },
}));

const FirstColumn = styled(Grid2)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    order: 1,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  fontWeight: 600,
  marginBottom: theme.spacing(2),
}));

const LinkText = styled(Typography)(({ theme }) => ({
  color: theme.footer?.textColor,
  marginBottom: theme.spacing(2),
}));

const SubLink = styled(Typography)(({ theme }) => ({
  color: theme.footer?.textColor,
}));

const DividerStyled = styled(Divider)(({ theme }) => ({
  borderColor: theme.footer?.dividerColor,
  margin: theme.spacing(11, 0, 11, 0),
  [theme.breakpoints.down("md")]: {
    margin: theme.spacing(6, 0, 11, 0),
  },
}));

const SubFooterContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: theme.spacing(4),
}));

interface TranslationColumn {
  href: string;
  title: string;
}

export const ColumnsLayoutFooter = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const { isRentablo } = useMandatorContext();

  const column1 = t("redesign.footer.column1.items", {
    returnObjects: true,
  }) as TranslationColumn[];
  const column2 = t("redesign.footer.column2.items", {
    returnObjects: true,
  }) as TranslationColumn[];
  const column3 = t("redesign.footer.column3.items", {
    returnObjects: true,
  }) as TranslationColumn[];
  const subFooterLinks = t("redesign.footer.subFooterItems", {
    returnObjects: true,
  }) as TranslationColumn[];

  return (
    <Wrapper>
      <Grid2Container container>
        <FirstColumn
          size={{
            xs: 12,
            sm: 5,
            md: 3,
          }}
        >
          <StyledLogo variant="footer" />
          {isRentablo && (
            <Copyright variant="body3">
              © {currentYear} Rentablo GmbH
            </Copyright>
          )}
          <Box sx={{ position: "relative" }}>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: theme.palette.background.footer,
                zIndex: 1,
              }}
            />
            <div className="pe-richsnippets" style={{ zIndex: 2 }}></div>
          </Box>
        </FirstColumn>
        {isRentablo && (
          <>
            <Grid2
              size={{
                xs: 12,
                sm: 4,
                md: 3,
              }}
            >
              <Title variant="body1">
                {t("redesign.footer.column1.title")}
              </Title>
              {column1.map((item) => (
                <Link href={item.href} key={item.title}>
                  <LinkText variant="body3">{item.title}</LinkText>
                </Link>
              ))}
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 4,
                md: 3,
              }}
            >
              <Title variant="body1">
                {t("redesign.footer.column2.title")}
              </Title>
              {column2.map((item) => (
                <Link href={item.href} key={item.title}>
                  <LinkText variant="body3">{item.title}</LinkText>
                </Link>
              ))}
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 4,
                md: 3,
              }}
            >
              <Title variant="body1">
                {t("redesign.footer.column3.title")}
              </Title>
              {column3.map((item) => (
                <Link href={item.href} key={item.title}>
                  <LinkText variant="body3">{item.title}</LinkText>
                </Link>
              ))}
            </Grid2>
          </>
        )}
      </Grid2Container>
      <DividerStyled />
      <SubFooterContainer>
        {subFooterLinks.map((item) => (
          <Link href={item.href} key={item.title}>
            <SubLink variant={isLessSm ? "caption" : "body2"}>
              {item.title}
            </SubLink>
          </Link>
        ))}
      </SubFooterContainer>
    </Wrapper>
  );
};
